"use strict";
// Extraction/validation/URL helpers for external research author identifiers.
// Shared by web (onboarding step + external-links modal) and API (onboarding +
// external-links sync) so both sides use the same canonical logic — no drift.
//
// These identifiers are OPTIONAL and author-level (not publication-level):
//   - Web of Science ResearcherID   → investigador.wos_researcher_id
//   - Scopus Author ID              → investigador.scopus_author_id
//
// Inputs are tolerant: the user may paste the raw id or the full profile link.
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractWosResearcherId = extractWosResearcherId;
exports.isValidWosResearcherId = isValidWosResearcherId;
exports.buildWosProfileUrl = buildWosProfileUrl;
exports.extractScopusAuthorId = extractScopusAuthorId;
exports.isValidScopusAuthorId = isValidScopusAuthorId;
exports.buildScopusProfileUrl = buildScopusProfileUrl;
exports.resolveWosProfile = resolveWosProfile;
exports.resolveScopusProfile = resolveScopusProfile;
// ── Web of Science ResearcherID (tolerant) ──────────────────────
// Accepts two shapes (per product decision):
//   1. Classic ResearcherID: A-1234-2011 / ABC-1234-2020  ([A-Z]{1,3}-\d{4}-\d{4})
//   2. Numeric author-record id from the modern link (…/wos/author/record/12345678)
// Returns the extracted id (ResearcherID upper-cased) or null.
function extractWosResearcherId(input) {
    const v = (input ?? '').trim();
    if (!v)
        return null;
    // 1) ResearcherID form anywhere in the string (raw id or inside a URL).
    const rid = v.match(/([A-Za-z]{1,3}-\d{4}-\d{4})/);
    if (rid)
        return rid[1].toUpperCase();
    // 2) Bare numeric record id.
    if (/^\d{6,}$/.test(v))
        return v;
    // 3) Numeric record id inside a Web of Science / Clarivate / ResearcherID URL.
    if (/webofscience|clarivate|researcherid/i.test(v)) {
        const fromRecord = v.match(/record\/(\d{6,})/i);
        if (fromRecord)
            return fromRecord[1];
        const anyDigits = v.match(/(\d{6,})/);
        if (anyDigits)
            return anyDigits[1];
    }
    return null;
}
function isValidWosResearcherId(input) {
    return extractWosResearcherId(input) !== null;
}
// Canonical Web of Science profile URL for a bare id. ResearcherIDs resolve under
// /rid/<id>; numeric record ids resolve under /record/<id>.
function buildWosProfileUrl(id) {
    return /^\d+$/.test(id)
        ? `https://www.webofscience.com/wos/author/record/${id}`
        : `https://www.webofscience.com/wos/author/rid/${id}`;
}
// ── Scopus Author ID ────────────────────────────────────────────
// Accepts the raw numeric id or a profile link
// (https://www.scopus.com/authid/detail.uri?authorId=7004212771).
function extractScopusAuthorId(input) {
    const v = (input ?? '').trim();
    if (!v)
        return null;
    // 1) Bare numeric id.
    if (/^\d{5,}$/.test(v))
        return v;
    // 2) authorId query param (full URL or raw "authorId=123").
    const param = v.match(/authorId=(\d{5,})/i);
    if (param)
        return param[1];
    // 3) Any long digit run inside a Scopus URL.
    if (/scopus/i.test(v)) {
        const anyDigits = v.match(/(\d{5,})/);
        if (anyDigits)
            return anyDigits[1];
    }
    return null;
}
function isValidScopusAuthorId(input) {
    return extractScopusAuthorId(input) !== null;
}
// `origin=inward` reproduce la navegación real de Scopus (el deep link "pelado"
// ?authorId= a veces no resuelve el perfil); sin partnerID de sesión, que es
// efímero y no debemos fabricar.
function buildScopusProfileUrl(authorId) {
    return `https://www.scopus.com/authid/detail.uri?authorId=${authorId}&origin=inward`;
}
function isHttpUrl(value) {
    return /^https?:\/\//i.test(value.trim());
}
function resolveWosProfile(input) {
    const id = extractWosResearcherId(input);
    if (!id)
        return null;
    const raw = (input ?? '').trim();
    return { id, url: isHttpUrl(raw) ? raw : buildWosProfileUrl(id) };
}
function resolveScopusProfile(input) {
    const id = extractScopusAuthorId(input);
    if (!id)
        return null;
    const raw = (input ?? '').trim();
    return { id, url: isHttpUrl(raw) ? raw : buildScopusProfileUrl(id) };
}
